import {
	isBinaryContentType,
	type OAuthFetchClient,
} from '../../custom-ui/bridge/methods/useProductFetchClient';
import type { MakeBridgeContext } from '../types';
import type { TBridgeMethod } from './types';
import { fg } from '@atlaskit/platform-feature-flags';
import { z } from 'zod';

const TEAMWORK_GRAPH_GATEWAY_PATH = '/gateway/api/graphql/twg'; // e.g. https://api.atlassian.com/gateway/api/graphql/twg

export type RequestTeamworkGraphPayload = {
	query: string;
	variables?: Record<string, unknown>;
};

const RequestTeamworkGraphPayloadSchema: z.ZodType<RequestTeamworkGraphPayload> = z.object({
	query: z.string(),
	variables: z.record(z.string(), z.unknown()).optional(),
});

export type RequestTeamworkGraphResponse = {
	body?: string;
	headers: Record<string, string>;
	isAttachment?: boolean;
	status: number;
	statusText: string;
};

type RequestTeamworkGraphContext = MakeBridgeContext<never, 'productFetchClient'>;

const teamworkGraphFetchClient = (
	productFetchClient: OAuthFetchClient,
	{ query, variables }: RequestTeamworkGraphPayload,
): Promise<Response> => {
	const body = JSON.stringify({ query, variables });
	const headers = new Headers({ 'content-type': 'application/json' });

	return productFetchClient(
		TEAMWORK_GRAPH_GATEWAY_PATH,
		'teamwork-graph',
		{ method: 'POST', headers, body },
		false,
	);
};

/**
 * Makes an authenticated call to the Teamwork Graph GraphQL API on behalf of the Forge app
 * running inside the iframe. Similar to fetchProduct, and uses the same productFetchClient.
 *
 * @param data.query - The raw graphql query
 * @param data.variables - The variables to pass to the graphql query
 */
export const requestTeamworkGraph: TBridgeMethod<
	RequestTeamworkGraphContext,
	RequestTeamworkGraphPayload,
	RequestTeamworkGraphResponse
> = async ({ services: { productFetchClient } }, payload) => {
	// TODO: Possibly double-gate behind gqlgw-twg-external-api?
	if (!fg('forge-ui-enable-requestteamworkgraph-bridge-method')) {
		throw new Error(
			'Forge requestTeamworkGraph (EAP) is not available for production apps. Please visit the documentation for EAP details https://developer.atlassian.com/platform/teamwork-graph/limitations-and-considerations/',
		);
	}

	const parsedPayload = RequestTeamworkGraphPayloadSchema.parse(payload);
	const response = await teamworkGraphFetchClient(productFetchClient, parsedPayload);

	const { status, statusText } = response;
	const headers: Record<string, string> = {};
	response.headers.forEach((value, key) => {
		headers[key] = value;
	});
	const isAttachment = isBinaryContentType(response.headers.get('content-type'));
	const responseBody = response.body ? await response.text() : undefined;

	return { headers, status, statusText, body: responseBody, isAttachment };
};
